import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  ComponentPropsWithoutRef,
  Fragment,
  forwardRef,
  isValidElement,
  ReactNode,
} from 'react';

import clsxm from '@/lib/clsxm';

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={clsxm(
        'z-[1100] max-w-xs overflow-hidden rounded-md bg-slate-900 px-2.5 py-1.5 text-xs text-white shadow-md',
        'whitespace-normal break-words',
        'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
        'dark:bg-slate-700 dark:text-slate-100',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';

/**
 * Drop-in replacement for antd's `<Tooltip title="...">`. Wraps the child in
 * Radix's Tooltip primitives. The child must be a single React element that
 * accepts a ref (use a native element or `forwardRef` component).
 *
 * Migrated from antd to avoid the @rc-component/portal + @rc-component/trigger
 * setState-during-commit loops that surfaced as React #185 on Win/Edge during
 * heavy chat re-renders.
 */
export interface SimpleTooltipProps {
  title: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alias for `side` to ease migration from antd's `<Tooltip placement>`. */
  placement?: 'top' | 'right' | 'bottom' | 'left' | string;
  delayDuration?: number;
  /** Set to false to render children unwrapped (no tooltip). */
  enabled?: boolean;
  /** Class applied to the tooltip content (matches antd's `className` prop). */
  className?: string;
}

export function SimpleTooltip({
  title,
  children,
  side,
  placement,
  delayDuration = 200,
  enabled = true,
  className,
}: SimpleTooltipProps) {
  if (!enabled || title === undefined || title === null || title === '') {
    return <>{children}</>;
  }
  // antd `placement` accepts top|bottom|left|right plus alignment variants
  // (topLeft, etc). Radix only has the four sides; anything else falls back
  // to the explicit `side` prop or the default `top`.
  const resolvedSide: 'top' | 'right' | 'bottom' | 'left' =
    side ??
    (placement === 'top' ||
    placement === 'right' ||
    placement === 'bottom' ||
    placement === 'left'
      ? placement
      : 'top');
  // Radix's `asChild` uses Children.only — it crashes if `children` is a
  // string, fragment with >1 child, multiple elements, or null/undefined.
  // antd's <Tooltip> was lenient about all of those. Wrap non-element-or-
  // fragment children in a span so the migration drop-in stays safe.
  const triggerChild =
    isValidElement(children) && children.type !== Fragment ? (
      children
    ) : (
      <span>{children}</span>
    );
  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{triggerChild}</TooltipTrigger>
      <TooltipContent side={resolvedSide} className={className}>
        {title}
      </TooltipContent>
    </TooltipRoot>
  );
}

export {
  TooltipContent,
  TooltipProvider,
  TooltipRoot as Tooltip,
  TooltipTrigger,
};
